import { useEffect, useMemo, useState } from "react";
import { previewReport, useContextHelp, useFlights, useRuns } from "../../api/hooks";
import type { PreviewResponse, ReportDef } from "../../api/types";
import { useSession } from "../../app/store";
import { Empty, JsonTree, Spinner, errText, fmtBytes, fmtTick } from "../../components/ui";
import { normaliseReport } from "./model";

export function PreviewPanel({
  projectId,
  report,
  outputName,
  templateDraft,
}: {
  projectId: string;
  report: ReportDef;
  outputName: string | null;
  templateDraft: string | null;
}) {
  const session = useSession();
  const flights = useFlights();
  const runs = useRuns(projectId);
  const help = useContextHelp();
  const [runId, setRunId] = useState<string | null>(session.currentRunId);
  const [flightId, setFlightId] = useState<string | null>(session.activeFlight);
  const [snapshotId, setSnapshotId] = useState<string | null>(null);
  const [auto, setAuto] = useState(true);
  const [res, setRes] = useState<PreviewResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<"artifact" | "values" | "snapshot" | "help">("artifact");
  const [artIdx, setArtIdx] = useState(0);

  const doneRuns = useMemo(() => (runs.data ?? []).filter((r) => r.status === "done"), [runs.data]);
  const run = doneRuns.find((r) => r.id === runId) ?? null;
  const flightChoices = run ? run.flight_ids : (flights.data ?? []).map((f) => f.id);

  useEffect(() => {
    if (flightId && !flightChoices.includes(flightId)) setFlightId(flightChoices[0] ?? null);
    if (!flightId && flightChoices.length) setFlightId(flightChoices[0]);
  }, [flightChoices, flightId]);

  const body = useMemo(
    () => ({
      report: normaliseReport(report),
      output: outputName,
      run_id: runId,
      flight_id: flightId,
      snapshot_id: snapshotId,
      template_override: templateDraft,
    }),
    [report, outputName, runId, flightId, snapshotId, templateDraft],
  );

  const go = async () => {
    if (!flightId && !runId) return;
    setBusy(true);
    setErr(null);
    try {
      setRes(await previewReport(projectId, body));
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const bodyKey = JSON.stringify(body);
  useEffect(() => {
    if (!auto) return;
    const h = setTimeout(go, 500);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyKey, auto]);

  const art = res?.artifacts[Math.min(artIdx, Math.max(0, (res?.artifacts.length ?? 1) - 1))];

  return (
    <div className="col" style={{ height: "100%", minHeight: 0 }}>
      <div className="row wrap" style={{ padding: "6px 10px", borderBottom: "1px solid var(--border)" }}>
        <label className="small dim">Run</label>
        <select className="inp sm" value={runId ?? ""} onChange={(e) => (setRunId(e.target.value || null), setSnapshotId(null))} style={{ maxWidth: 190 }}>
          <option value="">(none – synthetic snapshot)</option>
          {doneRuns.map((r) => (
            <option key={r.id} value={r.id}>
              {r.label || r.id} · {r.summary?.fires ?? 0} fires
            </option>
          ))}
        </select>
        <label className="small dim">Flight</label>
        <select className="inp sm" value={flightId ?? ""} onChange={(e) => (setFlightId(e.target.value || null), setSnapshotId(null))} style={{ maxWidth: 160 }}>
          {flightChoices.map((id) => {
            const f = flights.data?.find((x) => x.id === id);
            return (
              <option key={id} value={id}>
                {f?.tail ? `${f.tail} · ` : ""}
                {id}
              </option>
            );
          })}
        </select>
        {res?.candidates.length ? (
          <>
            <label className="small dim">Snapshot</label>
            <select className="inp sm" value={snapshotId ?? res.snapshot.id} onChange={(e) => setSnapshotId(e.target.value)} style={{ maxWidth: 220 }}>
              {res.candidates.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.trigger}/{c.label} @ {fmtTick(c.t)}
                </option>
              ))}
            </select>
          </>
        ) : null}
        <span className="spacer" />
        <label className="row small dim" style={{ gap: 4 }}>
          <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} /> live
        </label>
        <button className="btn sm primary" onClick={go} disabled={busy}>
          {busy ? <Spinner /> : "Render"}
        </button>
      </div>
      <div className="tabs">
        {(["artifact", "values", "snapshot", "help"] as const).map((t) => (
          <button key={t} className={tab === t ? "on" : ""} onClick={() => setTab(t)}>
            {t === "artifact" ? `Artifact${res?.artifacts.length ? ` (${res.artifacts.length})` : ""}` : t}
            {t === "values" && res?.values.length ? ` (${res.values.length})` : ""}
          </button>
        ))}
        {res?.synthetic ? (
          <span className="badge warn" title="No run snapshot for this report's triggers – rendering with a synthetic window at t=120 s">
            synthetic snapshot
          </span>
        ) : null}
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        {err ? <div className="problem err" style={{ margin: 10 }}>{err}</div> : null}
        {res?.errors.map((e, i) => (
          <details key={i} className="problem err" style={{ margin: 10 }} open>
            <summary>
              <b>{e.output}</b>: {e.error}
            </summary>
            <pre className="mono small">{e.traceback}</pre>
          </details>
        ))}
        {tab === "artifact" ? (
          !res ? (
            <Empty title="No preview yet">Pick a flight (and optionally a finished run) and press Render.</Empty>
          ) : !res.artifacts.length ? (
            <Empty title="No artifact">Every enabled output failed or none is enabled.</Empty>
          ) : (
            <div className="col" style={{ padding: 10 }}>
              <div className="row">
                {res.artifacts.length > 1
                  ? res.artifacts.map((a, i) => (
                      <button key={i} className={`btn sm${i === artIdx ? " primary" : ""}`} onClick={() => setArtIdx(i)}>
                        {a.output}
                      </button>
                    ))
                  : null}
                <span className="mono small dim">{art?.filename}</span>
                <span className="badge">{art?.media_type}</span>
                <span className="dim small">{fmtBytes(art?.size ?? 0)}</span>
                <span className="spacer" />
                <button
                  className="btn sm"
                  onClick={() => {
                    if (!art) return;
                    const blob = new Blob([art.text], { type: art.media_type });
                    const a = document.createElement("a");
                    a.href = URL.createObjectURL(blob);
                    a.download = art.filename;
                    a.click();
                    URL.revokeObjectURL(a.href);
                  }}
                >
                  Download
                </button>
              </div>
              <pre className="artifact mono">{art?.text}</pre>
            </div>
          )
        ) : null}
        {tab === "values" && res ? (
          <table className="tbl">
            <thead>
              <tr>
                <th>Token</th>
                <th>Key</th>
                <th>Source</th>
                <th>Value</th>
                <th>Formatted</th>
                <th>Units</th>
              </tr>
            </thead>
            <tbody>
              {res.values.map((v) => (
                <tr key={v.token}>
                  <td className="mono">{v.token}</td>
                  <td className="mono dim">{v.key}</td>
                  <td>
                    <span className="badge">{v.source}</span>
                  </td>
                  <td className="mono small">{summarise(v.value)}</td>
                  <td className="mono">{v.formatted}</td>
                  <td className="dim">{v.units ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {tab === "snapshot" && res ? (
          <div style={{ padding: 10 }}>
            <JsonTree value={res.snapshot} />
          </div>
        ) : null}
        {tab === "help" ? (
          <div className="col" style={{ padding: 10, gap: 12 }}>
            <HelpTable title="Jinja2 context" rows={(help.data?.jinja_context ?? []).map((r) => [r.name, r.type, r.doc])} head={["name", "type", "doc"]} />
            <HelpTable title="Jinja2 filters" rows={(help.data?.jinja_filters ?? []).map((r) => [r.name, r.doc])} head={["filter", "doc"]} />
            <HelpTable title="Bracket template tokens" rows={(help.data?.legacy_tokens ?? []).map((r) => [r.name, r.doc])} head={["token", "doc"]} />
            <div className="dim small">
              Python renderers: <code>def render(snapshot, ctx) -&gt; str | bytes</code> where <code>ctx</code> is the dict above (<code>ctx["values"]</code>,
              <code>ctx["rows"]</code>, <code>ctx["window"]</code>, <code>ctx["flight"]</code>, <code>ctx["inputs"]</code>, <code>ctx["p"](m)</code>, <code>ctx["agg"](m, how)</code>).
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function HelpTable({ title, rows, head }: { title: string; rows: string[][]; head: string[] }) {
  return (
    <div>
      <div className="section-title">{title}</div>
      <table className="tbl">
        <thead>
          <tr>
            {head.map((h) => (
              <th key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {r.map((c, j) => (
                <td key={j} className={j === 0 ? "mono" : j === r.length - 1 ? "dim" : "mono small"}>
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function summarise(v: unknown): string {
  if (v == null) return "null";
  if (Array.isArray(v)) return `[${v.length} items] ${JSON.stringify(v.slice(0, 6))}${v.length > 6 ? "…" : ""}`;
  if (typeof v === "object") return JSON.stringify(v).slice(0, 120);
  return String(v);
}
